import { useEffect, useMemo, useState } from 'react'
import { SharedProfile } from '../../lib/sharedProfile'
import { fetchSchoolPrograms, ProgramTuition } from '../../services/gemini/tuition'

const ONTARIO_SCHOOLS = [
    "Algonquin College", "Brock University", "Carleton University",
    "Centennial College", "Conestoga College", "Durham College",
    "Fanshawe College", "Fleming College", "George Brown College",
    "Georgian College", "Humber College", "Lakehead University",
    "McMaster University", "Mohawk College", "Niagara College",
    "Nipissing University", "Ontario Tech University", "Queen's University",
    "Sault College", "Seneca College", "Sheridan College",
    "St. Clair College", "St. Lawrence College",
    "Toronto Metropolitan University", "Trent University",
    "University of Guelph", "University of Ottawa", "University of Toronto",
    "University of Waterloo", "University of Windsor", "Western University",
    "Wilfrid Laurier University", "York University",
]

interface Props {
    onComplete: (profile: SharedProfile) => void
    presetStudyInfo?: {
        school: string
        program: string
        annualTuition: number | null
    }
    lockStudyInfo?: boolean
}

export default function CombinedWizard({
    onComplete,
    presetStudyInfo,
    lockStudyInfo = false,
}: Props) {
    const isExtensionMode =
        !!lockStudyInfo &&
        !!presetStudyInfo?.school &&
        !!presetStudyInfo?.program

    const steps = useMemo(
        () =>
            isExtensionMode
                ? ['Study Details', 'Finances', 'Background']
                : ['School', 'Program', 'Study Details', 'Finances', 'Background'],
        [isExtensionMode]
    )

    const [step, setStep] = useState(0)
    const [schoolInput, setSchoolInput] = useState(presetStudyInfo?.school ?? '')
    const [showDropdown, setShowDropdown] = useState(false)
    const [programs, setPrograms] = useState<ProgramTuition[]>([])
    const [loadingPrograms, setLoadingPrograms] = useState(false)

    const [profile, setProfile] = useState<Partial<SharedProfile>>({
        school: presetStudyInfo?.school ?? '',
        program: presetStudyInfo?.program ?? '',
        annualTuition: presetStudyInfo?.annualTuition ?? null,
        demographicFlags: [],
        ontarioResident: true,
        worked24MonthsInARow: false,
        fixedContributionWaived: false,
        hasDependants: false,
        status: 'single',
        programLevel: 'undergraduate',
        studyLoad: 'full_time',
        studyWeeks: 34,
        numberOfTerms: 2,
        booksSupplies: 0,
        livingArrangement: 'off_campus_regular',
        studentIncome: 0,
        studentAssets: 0,
        parentIncome: 0,
        incomeMax: null,
        age: 19,
        yearsOutOfHighSchool: 1,
    })

    useEffect(() => {
        if (presetStudyInfo?.school) {
            setSchoolInput(presetStudyInfo.school)
        }

        if (presetStudyInfo) {
            setProfile(prev => ({
                ...prev,
                school: presetStudyInfo.school,
                program: presetStudyInfo.program,
                annualTuition: presetStudyInfo.annualTuition,
            }))
        }
    }, [presetStudyInfo])

    const update = (key: keyof SharedProfile, value: any) =>
        setProfile(prev => ({ ...prev, [key]: value }))

    const goNext = () => setStep(s => Math.min(s + 1, steps.length - 1))
    const goBack = () => setStep(s => Math.max(s - 1, 0))

    const handleSchoolNext = async () => {
        if (!profile.school) return

        setShowDropdown(false)
        setLoadingPrograms(true)
        goNext()

        try {
            const fetched = await fetchSchoolPrograms(profile.school)
            setPrograms(fetched)
        } catch (error) {
            console.error('failed to fetch school programs', error)
            setPrograms([])
        } finally {
            setLoadingPrograms(false)
        }
    }

    const inputClass =
        'w-full bg-white border border-gray-200 rounded-2xl px-4 py-3.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder-gray-400 shadow-sm'

    const selectClass =
        'w-full bg-white border border-gray-200 rounded-2xl px-4 py-3.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm appearance-none'

    const labelClass = 'block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5'

    const renderLockedStudyInfo = isExtensionMode ? (
        <div className="mb-6 rounded-3xl border border-blue-100 bg-blue-50 px-4 py-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-blue-600 mb-3">
                Detected from extension
            </p>
            <div className="space-y-2">
                <div>
                    <p className="text-xs text-gray-500">School</p>
                    <p className="text-sm font-semibold text-gray-900">{presetStudyInfo?.school}</p>
                </div>
                <div>
                    <p className="text-xs text-gray-500">Program</p>
                    <p className="text-sm font-semibold text-gray-900">{presetStudyInfo?.program}</p>
                </div>
                <div>
                    <p className="text-xs text-gray-500">Annual tuition</p>
                    <p className="text-sm font-semibold text-emerald-700">
                        {presetStudyInfo?.annualTuition != null
                            ? `$${presetStudyInfo.annualTuition.toLocaleString()}`
                            : 'Not available'}
                    </p>
                </div>
            </div>
        </div>
    ) : null

    const canSubmit =
        !!profile.school &&
        !!profile.program &&
        profile.annualTuition !== null &&
        profile.annualTuition !== undefined

    return (
        <div style={{ fontFamily: "'DM Sans', sans-serif" }} className="max-w-lg mx-auto">
            <link
                href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap"
                rel="stylesheet"
            />

            <div className="px-6 mb-8">
                <div className="flex gap-1.5 mb-3">
                    {steps.map((s, i) => (
                        <div key={s} className="flex-1 h-1 rounded-full overflow-hidden bg-gray-100">
                            <div
                                className="h-full rounded-full transition-all duration-500"
                                style={{
                                    width: i < step ? '100%' : i === step ? '60%' : '0%',
                                    background: i <= step ? '#3b82f6' : 'transparent',
                                }}
                            />
                        </div>
                    ))}
                </div>
                <p className="text-xs text-gray-400 font-medium">
                    Step {step + 1} of {steps.length} — <span className="text-gray-600">{steps[step]}</span>
                </p>
            </div>

            <div className="px-6 pb-10">
                {renderLockedStudyInfo}

                {!isExtensionMode && step === 0 && (
                    <div>
                        <div className="mb-7">
                            <h2 className="text-2xl font-bold text-gray-900 mb-1">Where do you study?</h2>
                            <p className="text-sm text-gray-500">We'll look up real tuition data for your school</p>
                        </div>

                        <div className="relative mb-6">
                            <label className={labelClass}>School name</label>
                            <input
                                type="text"
                                value={schoolInput}
                                onChange={e => {
                                    setSchoolInput(e.target.value)
                                    update('school', e.target.value)
                                    setShowDropdown(true)
                                }}
                                onFocus={() => setShowDropdown(true)}
                                placeholder="e.g. George Brown College"
                                className={inputClass}
                            />

                            {showDropdown && schoolInput.length > 1 && (
                                <ul className="absolute z-20 w-full mt-1 bg-white border border-gray-100 rounded-2xl shadow-xl overflow-hidden max-h-56 overflow-y-auto">
                                    {ONTARIO_SCHOOLS
                                        .filter(s => s.toLowerCase().includes(schoolInput.toLowerCase()))
                                        .map(school => (
                                            <li
                                                key={school}
                                                onMouseDown={() => {
                                                    update('school', school)
                                                    setSchoolInput(school)
                                                    setShowDropdown(false)
                                                }}
                                                className="px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 cursor-pointer transition-colors border-b border-gray-50 last:border-0"
                                            >
                                                {school}
                                            </li>
                                        ))}
                                </ul>
                            )}
                        </div>

                        <button
                            onClick={handleSchoolNext}
                            disabled={!profile.school}
                            className="w-full py-4 rounded-2xl text-sm font-semibold transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                            style={{
                                background: profile.school ? '#3b82f6' : '#e5e7eb',
                                color: profile.school ? 'white' : '#9ca3af',
                            }}
                        >
                            Continue →
                        </button>
                    </div>
                )}

                {!isExtensionMode && step === 1 && (
                    <div>
                        <div className="mb-7">
                            <h2 className="text-2xl font-bold text-gray-900 mb-1">What's your program?</h2>
                            <p className="text-sm text-gray-500">Selecting a program auto-fills your tuition</p>
                        </div>

                        {loadingPrograms ? (
                            <div className="flex flex-col items-center justify-center py-16 gap-3">
                                <div className="w-8 h-8 border-[3px] border-blue-200 border-t-blue-500 rounded-full animate-spin" />
                                <p className="text-sm text-gray-500">Loading programs from {profile.school}...</p>
                            </div>
                        ) : programs.length > 0 ? (
                            <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1 mb-4">
                                {programs.map(p => (
                                    <button
                                        key={p.name}
                                        onClick={() => {
                                            update('program', p.name)
                                            update('annualTuition', p.annualTuition)
                                            goNext()
                                        }}
                                        className="w-full text-left px-4 py-4 bg-white border border-gray-200 rounded-2xl hover:border-blue-400 hover:bg-blue-50 transition-all shadow-sm group"
                                    >
                                        <span className="block text-sm font-semibold text-gray-800 group-hover:text-blue-700">
                                            {p.name}
                                        </span>
                                        <span className="block text-xs text-emerald-600 mt-0.5 font-medium">
                                            ~${p.annualTuition.toLocaleString()} / year
                                        </span>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="space-y-4 mb-6">
                                <div className="bg-amber-50 border border-amber-100 rounded-2xl px-4 py-3 text-sm text-amber-700">
                                    Couldn't load programs automatically — enter manually below
                                </div>

                                <div>
                                    <label className={labelClass}>Program name</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Computer Science"
                                        onBlur={e => update('program', e.target.value)}
                                        className={inputClass}
                                    />
                                </div>

                                <div>
                                    <label className={labelClass}>Annual tuition ($)</label>
                                    <input
                                        type="number"
                                        placeholder="e.g. 7500"
                                        onBlur={e => update('annualTuition', parseInt(e.target.value) || null)}
                                        className={inputClass}
                                    />
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        onClick={goBack}
                                        className="flex-1 py-4 border border-gray-200 rounded-2xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all"
                                    >
                                        Back
                                    </button>
                                    <button
                                        onClick={goNext}
                                        className="flex-1 py-4 bg-blue-500 text-white rounded-2xl text-sm font-semibold hover:bg-blue-600 transition-all"
                                    >
                                        Continue →
                                    </button>
                                </div>
                            </div>
                        )}

                        <button
                            onClick={goBack}
                            className="w-full py-2.5 text-sm text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            ← Back
                        </button>
                    </div>
                )}

                {((isExtensionMode && step === 0) || (!isExtensionMode && step === 2)) && (
                    <div>
                        <div className="mb-7">
                            <h2 className="text-2xl font-bold text-gray-900 mb-1">Study details</h2>
                            <p className="text-sm text-gray-500">Used to calculate your OSAP amount</p>
                        </div>

                        <div className="space-y-4 mb-7">
                            <div>
                                <label className={labelClass}>Program level</label>
                                <div className="relative">
                                    <select
                                        value={profile.programLevel}
                                        onChange={e => update('programLevel', e.target.value)}
                                        className={selectClass}
                                    >
                                        <option value="undergraduate">Undergraduate</option>
                                        <option value="graduate">Graduate</option>
                                        <option value="college">College</option>
                                        <option value="professional">Professional</option>
                                    </select>
                                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-xs">▼</span>
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>Study load</label>
                                <div className="flex gap-3">
                                    {[
                                        { value: 'full_time', label: 'Full time' },
                                        { value: 'part_time', label: 'Part time' },
                                    ].map(opt => (
                                        <button
                                            key={opt.value}
                                            onClick={() => update('studyLoad', opt.value)}
                                            className={`flex-1 py-3.5 rounded-2xl text-sm font-semibold border transition-all ${
                                                profile.studyLoad === opt.value
                                                    ? 'bg-blue-500 text-white border-blue-500 shadow-sm'
                                                    : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
                                            }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>Living arrangement</label>
                                <div className="relative">
                                    <select
                                        value={profile.livingArrangement}
                                        onChange={e => update('livingArrangement', e.target.value)}
                                        className={selectClass}
                                    >
                                        <option value="with_parents">Living with parents</option>
                                        <option value="on_campus">On-campus residence</option>
                                        <option value="off_campus_regular">Off-campus (outside Toronto)</option>
                                        <option value="off_campus_toronto">Off-campus (Toronto / Ottawa)</option>
                                    </select>
                                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-xs">▼</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={goBack}
                                disabled={step === 0}
                                className="flex-1 py-4 border border-gray-200 rounded-2xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                Back
                            </button>
                            <button
                                onClick={goNext}
                                className="flex-1 py-4 bg-blue-500 text-white rounded-2xl text-sm font-semibold hover:bg-blue-600 transition-all"
                            >
                                Continue →
                            </button>
                        </div>
                    </div>
                )}

                {((isExtensionMode && step === 1) || (!isExtensionMode && step === 3)) && (
                    <div>
                        <div className="mb-7">
                            <h2 className="text-2xl font-bold text-gray-900 mb-1">Your finances</h2>
                            <p className="text-sm text-gray-500">Used for OSAP calculation and scholarship matching</p>
                        </div>

                        <div className="space-y-4 mb-7">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelClass}>Your age</label>
                                    <input
                                        type="number"
                                        placeholder="19"
                                        defaultValue={profile.age ?? ''}
                                        onBlur={e => update('age', parseInt(e.target.value) || 19)}
                                        className={inputClass}
                                    />
                                </div>

                                <div>
                                    <label className={labelClass}>Years since high school</label>
                                    <input
                                        type="number"
                                        placeholder="1"
                                        defaultValue={profile.yearsOutOfHighSchool ?? ''}
                                        onBlur={e => update('yearsOutOfHighSchool', parseInt(e.target.value) || 1)}
                                        className={inputClass}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>Your income last year ($)</label>
                                <input
                                    type="number"
                                    placeholder="0"
                                    defaultValue={profile.studentIncome ?? ''}
                                    onBlur={e => update('studentIncome', parseInt(e.target.value) || 0)}
                                    className={inputClass}
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Parent(s) combined income ($)</label>
                                <input
                                    type="number"
                                    placeholder="60000"
                                    defaultValue={profile.parentIncome ?? ''}
                                    onBlur={e => update('parentIncome', parseInt(e.target.value) || 0)}
                                    className={inputClass}
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Family income bracket</label>
                                <div className="relative">
                                    <select
                                        onChange={e => update('incomeMax', parseInt(e.target.value) || null)}
                                        className={selectClass}
                                        defaultValue={profile.incomeMax ?? ''}
                                    >
                                        <option value="30000">Under $30,000</option>
                                        <option value="50000">$30,000 – $50,000</option>
                                        <option value="75000">$50,000 – $75,000</option>
                                        <option value="">Prefer not to say</option>
                                    </select>
                                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-xs">▼</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={goBack}
                                className="flex-1 py-4 border border-gray-200 rounded-2xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all"
                            >
                                Back
                            </button>
                            <button
                                onClick={goNext}
                                className="flex-1 py-4 bg-blue-500 text-white rounded-2xl text-sm font-semibold hover:bg-blue-600 transition-all"
                            >
                                Continue →
                            </button>
                        </div>
                    </div>
                )}

                {((isExtensionMode && step === 2) || (!isExtensionMode && step === 4)) && (
                    <div>
                        <div className="mb-7">
                            <h2 className="text-2xl font-bold text-gray-900 mb-1">Background</h2>
                            <p className="text-sm text-gray-500">
                                Select all that apply — unlocks additional scholarships
                            </p>
                        </div>

                        <div className="space-y-2 mb-7">
                            {[
                                { value: 'indigenous', label: 'Indigenous', desc: 'First Nations, Métis, or Inuit' },
                                { value: 'metis', label: 'Métis Nation of Ontario', desc: 'Registered with MNO' },
                                { value: 'disability', label: 'Student with Disability', desc: 'Registered with accessibility office' },
                                { value: 'first-gen', label: 'First Generation', desc: 'First in family to attend post-secondary' },
                                { value: 'cas-care', label: 'Former Crown Ward', desc: 'Previously in CAS care' },
                                { value: 'francophone', label: 'Francophone', desc: 'French-language learner' },
                            ].map(flag => {
                                const selected = profile.demographicFlags?.includes(flag.value)

                                return (
                                    <button
                                        key={flag.value}
                                        onClick={() => {
                                            const current = profile.demographicFlags || []
                                            update(
                                                'demographicFlags',
                                                selected
                                                    ? current.filter(f => f !== flag.value)
                                                    : [...current, flag.value]
                                            )
                                        }}
                                        className={`w-full text-left px-4 py-4 rounded-2xl border transition-all ${
                                            selected
                                                ? 'bg-blue-50 border-blue-400 shadow-sm'
                                                : 'bg-white border-gray-200 hover:border-gray-300'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <span className={`block text-sm font-semibold ${selected ? 'text-blue-700' : 'text-gray-800'}`}>
                                                    {flag.label}
                                                </span>
                                                <span className="block text-xs text-gray-500 mt-0.5">{flag.desc}</span>
                                            </div>

                                            <div
                                                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-3 transition-all ${
                                                    selected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                                                }`}
                                            >
                                                {selected && (
                                                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                    </svg>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                )
                            })}
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={goBack}
                                className="flex-1 py-4 border border-gray-200 rounded-2xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all"
                            >
                                Back
                            </button>
                            <button
                                onClick={() => {
                                    if (!canSubmit) return
                                    onComplete(profile as SharedProfile)
                                }}
                                disabled={!canSubmit}
                                className="flex-1 py-4 bg-blue-500 text-white rounded-2xl text-sm font-semibold hover:bg-blue-600 transition-all shadow-md disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Find My Funding →
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}